import React, { useCallback } from "react";
import {
  Box,
  Table as CKTable,
  Link,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  HStack,
  Badge,
  Card,
  CardBody,
  Divider,
  Icon,
  Flex,
} from "@chakra-ui/react";
import * as Styled from "./styles";
import { UsuallyContext } from "@context/usuallyContex";
import { OportunityContext } from "@context/oportunityContex";
import oportunityService from "@services/Oportunity/OportunityService";
import { FaAward, FaSortDown, FaSortUp } from "react-icons/fa";
import { theme } from "src/styles/theme";
import { useNavigate } from "react-router-dom";
import { FaRegSquareCheck } from "react-icons/fa6";
import { IoTrashOutline } from "react-icons/io5";
import ButtonDropdown from "./ButtonDropdown";
import { TbCalendarPlus } from "react-icons/tb";
import { BiMessageRoundedAdd } from "react-icons/bi";
import { RiLogoutCircleRLine } from "react-icons/ri";
import oportunityServiceInstance from "@services/Oportunity/OportunityService";
import { useMessageErrorToast } from "@hook/useMessageErrorToast";
import { useOnlyMessageToast } from "@hook/useMessageToast";
import Spinner from "@components/common/Spinner";

type TableRow = {
  id: string;
  fase: string;
  status: string;
  nomeDaConta: string;
  nomeDaUnidade: string;
  oportunidadeId: string;
  opportunityIdSalesforce: string;
  idEmpreendimentoSales: string;
  imobiliaria: string;
  imobiliariaId: string;
  ranking: string | null;
  origemConta: string;
  dataUltimaModificacao: string;
  empreendimentoDeInteresse: string;
};

interface IDataTable {
  data: TableRow[];
  onClick: () => void;
  searchFilter: string;
  fase: string | string[];
}

function TableMyOportunity({
  data,
  onClick,
  searchFilter,
  fase,
}: Readonly<IDataTable>) {
  const navigate = useNavigate();
  const displayMessageError = useMessageErrorToast();
  const displayMessage = useOnlyMessageToast();
  const idCorretor: any = localStorage.getItem("@idCorretor");
  const { setHashSalesSidebar, setHashOportunity, setHashDetailOportunity } =
    React.useContext(UsuallyContext);
  const {
    setLoading,
    setContracts,
    setAccessCreateOp,
    setFaseOp,
    setImobiliariaId,
    setImobiliariaNome,
    setIdEmpreendimentoSales,
    setNomeEmpreendimentoSales,
    setIndexTabDetails,
    setIndexTabActivityComponent,
    setAccessQuote,
  } = React.useContext(OportunityContext);
  const [loadingCreateCall, setLoadingCreateCall] = React.useState(false);
  const [sortConfig, setSortConfig] = React.useState<{
    key: keyof TableRow;
    direction: "ascending" | "descending";
  }>({ key: "fase", direction: "ascending" });

  const filteredRows = React.useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    // Converter fase para array se for string
    const faseArray = Array.isArray(fase) ? fase : fase ? [fase] : [];

    let filteredData = data.filter((row) => {
      // Filtro de busca por texto
      const lowerCaseFilter = searchFilter.toLowerCase();
      const isMatchingSearch =
        !searchFilter ||
        row.nomeDaConta?.toLowerCase().includes(lowerCaseFilter) ||
        row.oportunidadeId?.toLowerCase().includes(lowerCaseFilter) ||
        row.nomeDaUnidade?.toLowerCase().includes(lowerCaseFilter) ||
        row.empreendimentoDeInteresse
          ?.toLowerCase()
          .includes(lowerCaseFilter) ||
        row.origemConta?.toLowerCase().includes(lowerCaseFilter) ||
        row.dataUltimaModificacao?.toLowerCase().includes(lowerCaseFilter);

      // Se não passou no filtro de busca, já elimina
      if (!isMatchingSearch) {
        return false;
      }

      // CORREÇÃO: Para filtros de ranking, não aplicamos filtro de fase
      // pois os dados já vêm filtrados da API
      const isRankingFilter = faseArray.some((item) => {
        const itemLower = item?.toLowerCase() || "";
        return (
          itemLower.includes("diamante") ||
          itemLower.includes("ouro") ||
          itemLower.includes("prata") ||
          itemLower.includes("bronze") ||
          itemLower.includes("aço") ||
          itemLower.includes("não elegível") ||
          itemLower.includes("ranking não disponível")
        );
      });

      const isSpecialFilter = faseArray.some((item) => {
        const itemLower = item?.toLowerCase() || "";
        return (
          itemLower.includes("oportunidades vencidas") ||
          // itemLower.includes("contrato") ||
          itemLower.includes("cliente ouro")
        );
      });

      // Para filtros de ranking ou especiais, retorna todos os dados
      // pois a filtragem já foi feita na API
      if (isRankingFilter || isSpecialFilter) {
        return true;
      }

      // Se não há fase definida ou array vazio, retorna todos
      if (!fase || (Array.isArray(fase) && fase.length === 0)) {
        return true;
      }

      // Para outros filtros, aplica a lógica normal de fase
      const rowFase = row.fase?.toLowerCase() || "";

      const isMatchingPhase = faseArray.some((faseItem) => {
        const lowerFase = faseItem?.toLowerCase() || "";

        // Se fase é vazia ou "todas", retorna true
        if (lowerFase === "" || lowerFase === "todas") {
          return true;
        }

        // Comparação direta da fase
        const directMatch = rowFase === lowerFase;

        return directMatch;
      });

      return isMatchingPhase;
    });

    // Remover duplicatas antes da ordenação
    filteredData = Array.from(
      new Set(filteredData.map((row) => row.oportunidadeId))
    )
      .map((id) => filteredData.find((row) => row.oportunidadeId === id))
      .filter(Boolean) as TableRow[];

    if (sortConfig !== null) {
      filteredData = filteredData.sort((a, b) => {
        const aValue = a[sortConfig.key] || "";
        const bValue = b[sortConfig.key] || "";

        if (aValue === "" && bValue !== "") {
          return 1;
        }
        if (aValue !== "" && bValue === "") {
          return -1;
        }

        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredData;
  }, [data, fase, searchFilter, sortConfig]);

  const requestSort = (key: keyof TableRow) => {
    let direction: "ascending" | "descending" = "ascending";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "ascending"
    ) {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleClickId = (
    opportunityIdSalesforce: string,
    oportunidadeId: string,
    oportunidadeName: string
  ) => {
    localStorage.removeItem("@notificationNavigation");
    localStorage.removeItem("@indexTabDetails");
    localStorage.removeItem("@indexTabActivityComponent");
    localStorage.removeItem("@notificationType");

    setAccessCreateOp({
      oportunidadeId: oportunidadeId,
      nomeDaConta: oportunidadeName,
      oportunityIdSalesforce: opportunityIdSalesforce,
    });
    setLoading(true);

    oportunityService
      .getContracts({ opportunityIdSalesforce: opportunityIdSalesforce })
      .then((c) => {
        setContracts(c);
        setLoading(false);
      });

    navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
      state: {
        oportunidadeId,
      },
    });
    onClick();

    setHashDetailOportunity("DetailOportunity");
    setHashOportunity("DetailOportunityComponent");
    setHashSalesSidebar("DetailOportunityComponent");
    setHashDetailOportunity("DetailOportunity");
    setIndexTabDetails(0);
  };

  const getHeaderClass = (key: keyof TableRow) => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === key
      ? sortConfig.direction === "ascending"
        ? "sorted-asc"
        : "sorted-desc"
      : undefined;
  };

  const renderSortIcon = (key: keyof TableRow) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "ascending" ? (
        <Styled.Icons>
          <FaSortUp />
        </Styled.Icons>
      ) : (
        <Styled.Icons>
          <FaSortDown />
        </Styled.Icons>
      );
    }
    return null;
  };

  const handleAdjustFase = (value: string) => {
    if (value?.toLowerCase() === "aguardando atendimento corretor")
      return "Aguardando atendimento";
    return value;
  };

  const handleRanking = (ranking: string) => {
    if (ranking === "Diamante") return "#3DB4AE";
    if (ranking === "Ouro") return "#E69C08";
    else if (ranking === "Prata") return "#878D97";
    else if (ranking === "Bronze") return "#BD6E3D";
    else if (ranking === "Aço") return "#445571";
    else if (ranking === "Não elegível") return "#CFCFCF";
    else if (ranking === "Ranking não disponível") return "#59667A";
    else return undefined;
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return dateString;

    const date = new Date(dateString);
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  };

  const getFaseColors = (fase: string) => {
    const lowerFase = fase?.toLowerCase();

    if (
      lowerFase === "cancelada" ||
      lowerFase === "cancelado" ||
      lowerFase?.includes("perdido")
    ) {
      return {
        bg: "#FCF6F6",
        color: "#5E1E31",
        borderColor: "#5E1E31",
      };
    }

    if (lowerFase === "aguardando atendimento corretor") {
      return {
        bg: "#F5F7FA",
        color: theme.text50,
        borderColor: theme.text50,
      };
    }

    return {
      bg: "#F0F4FD",
      color: theme.primary,
      borderColor: theme.primary,
    };
  };

  const handleCreateTasks = useCallback(
    async (dropdownOp: any) => {
      setLoadingCreateCall(true);

      const getDateToDay = () => {
        const today = new Date();
        today.setDate(today.getDate());
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        return `${day}/${month}/${year}`;
      };

      try {
        const response = await oportunityServiceInstance.createTask({
          idCorretorSalesforce: idCorretor,
          assunto: "Chamada",
          dataDeVencimento: `${getDateToDay()}`,
          relativoA: dropdownOp.opportunityIdSalesforce,
          status: "Concluído",
          tipoDeRegistro: "Call",
          numeroDeTentativas: 1,
          comentarios:
            "Chamada criada automaticamente pelo corretor, através do botão de whatsapp do cliente no Bora Vender.",
        });

        if (response.status >= 400 && response?.status !== 401) {
          setLoadingCreateCall(false);
          displayMessageError({
            message: response?.data?.mensagem
              ? response?.data?.mensagem
              : `Erro ao criar chamada!`,
          });
          console.log("status 400", response);
          return;
        }
        if (response.status === 401) {
          setLoadingCreateCall(false);
          // setIsUnauthorized(true);
          return;
        }

        displayMessage("Chamada registrada com sucesso!");
        setLoadingCreateCall(false);
      } catch (error) {
        setLoadingCreateCall(false);
        console.log("errorHandleCreateTask", error);
      }
    },
    [displayMessage, displayMessageError, idCorretor]
  );

  const handleClickButtonDropdown = React.useCallback(
    async (dropdownOp: any, titleDropdown: string) => {
      try {
        // Definir o estado do tab para 0 antes da navegação
        setIndexTabDetails(0);
        setIndexTabActivityComponent(0);

        // Persistir os valores no localStorage para recuperá-los após a navegação
        localStorage.setItem("@indexTabDetails", "0");
        localStorage.setItem("@indexTabActivityComponent", "0");
        localStorage.setItem("@notificationNavigation", "true");
        localStorage.setItem("@notificationType", dropdownOp?.tipoNotificacao);

        // Configurar contextos comuns para todos os tipos de notificação
        setHashOportunity("DetailOportunityComponent");

        // Configurar o AccessCreateOp com o ID da oportunidade
        setAccessCreateOp((prevState: any) => ({
          ...prevState,
          oportunidadeId: dropdownOp?.oportunidadeId,
          oportunityIdSalesforce:
            dropdownOp?.opportunityIdSalesforce ||
            prevState.oportunityIdSalesforce,
        }));

        // Configurações específicas por tipo de notificação
        if (titleDropdown === "Criar agendamento") {
          setHashDetailOportunity("DetailOportunity");

          // Navegar para a página de detalhes com o ID da oportunidade
          navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
            replace: true,
            state: {
              source: "dropdownOp",
              type: "CriarAgendamento",
              indexTabDetails: 2,
              indexTabActivityComponent: 1,
              oportunidadeId: dropdownOp?.oportunidadeId,
            },
          });
        } else if (titleDropdown === "Abrir Whatsapp") {
          let phone = dropdownOp?.telefonePrincipal?.replace(/\D/g, "");

          if (!phone) {
            console.warn("Telefone inválido ou inexistente");
            return;
          }

          // Garante que sempre vai ter DDI 55
          if (!phone.startsWith("55")) {
            phone = "55" + phone;
          }

          // Detecta mobile
          const isMobile = /Android|iPhone|iPad|iPod/i.test(
            navigator.userAgent
          );

          void handleCreateTasks(dropdownOp);

          if (isMobile) {
            // Mobile → abre app do WhatsApp
            window.open(`https://wa.me/${phone}`, "_blank");
          } else {
            // Desktop → WhatsApp Web
            window.open(
              `https://web.whatsapp.com/send?phone=${phone}`,
              "_blank"
            );
          }
        } else if (titleDropdown === "Criar tarefa") {
          setHashDetailOportunity("DetailOportunity");

          // Navegar para a página de detalhes com o ID da oportunidade
          navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
            replace: true,
            state: {
              source: "dropdownOp",
              type: "CriarTarefa",
              indexTabDetails: 2,
              indexTabActivityComponent: 0,
              oportunidadeId: dropdownOp?.oportunidadeId,
            },
          });
        } else if (titleDropdown === "Fechar e perder oportunidade") {
          setHashDetailOportunity("DetailOportunity");

          // Navegar para a página de detalhes com o ID da oportunidade
          navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
            replace: true,
            state: {
              source: "dropdownOp",
              type: "FecharPerder",
              indexTabDetails: 0,
              indexTabActivityComponent: 0,
              oportunidadeId: dropdownOp?.oportunidadeId,
            },
          });
        } else {
          // Tipo RetornoCotacao
          setHashDetailOportunity("DetailOportunity");

          setAccessQuote({
            nomeDaCotacao: dropdownOp?.nomeDaCotacao,
            idCotacaoSalesforce: dropdownOp?.idCotacaoSalesforce,
          });

          // Navegar para a página de detalhes com o ID da oportunidade
          navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
            replace: true,
            state: {
              source: "dropdownOp",
              type: "CriarCotacao",
              indexTabDetails: 4,
              indexTabActivityComponent: 0,
              oportunidadeId: dropdownOp?.oportunidadeId,
            },
          });
        }
      } catch (error) {
        console.error(
          "Erro ao processar o redirecionamento do dropdown:",
          error
        );
      }
    },
    [
      handleCreateTasks,
      navigate,
      setAccessCreateOp,
      setAccessQuote,
      setHashDetailOportunity,
      setHashOportunity,
      setIndexTabActivityComponent,
      setIndexTabDetails,
    ]
  );

  const arrayButtons = [
    {
      icon: <TbCalendarPlus fontSize={"18px"} />,
      title: `Criar agendamento`,
    },
    {
      icon: <BiMessageRoundedAdd fontSize={"18px"} />,
      title: `Abrir Whatsapp`,
    },
    {
      icon: <FaRegSquareCheck fontSize={"18px"} />,
      title: `Criar tarefa`,
    },
    {
      icon: <RiLogoutCircleRLine fontSize={"18px"} />,
      title: `Criar cotação`,
    },
    {
      icon: <IoTrashOutline fontSize={"18px"} />,
      title: `Fechar e perder oportunidade`,
    },
  ];

  return (
    <>
      <Box
        overflowX="hidden"
        overflowY={"hidden"}
        display={{ base: "none", md: "block" }}
        minH={{ base: "400px", xl: "330px", "2xl": "620px" }}
      >
        <CKTable flexDirection="column" variant="simple" bg="white">
          <Thead
            h="60px"
            position="sticky"
            top="0"
            zIndex="0"
            bg="white"
            boxShadow="sm"
          >
            <Tr>
              <Th minW="150px" textAlign="center" color={theme.text}>
                <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                  <Text
                    alignItems={"center"}
                    fontSize={11}
                    className={getHeaderClass("nomeDaConta")}
                    onClick={() => requestSort("nomeDaConta")}
                    w={"fit-content"}
                    cursor={"pointer"}
                  >
                    Nome do cliente{" "}
                  </Text>
                  {renderSortIcon("nomeDaConta")}
                </Box>
              </Th>
              <Th minW="150px" textAlign="center" color={theme.text}>
                <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                  <Text
                    alignItems={"center"}
                    fontSize={11}
                    className={getHeaderClass("oportunidadeId")}
                    onClick={() => requestSort("oportunidadeId")}
                    w={"fit-content"}
                    cursor={"pointer"}
                  >
                    ID da oportunidade{" "}
                  </Text>
                  {renderSortIcon("oportunidadeId")}
                </Box>
              </Th>
              <Th minW="150px" color={theme.text}>
                <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                  <Text
                    alignItems={"center"}
                    fontSize={11}
                    className={getHeaderClass("fase")}
                    onClick={() => requestSort("fase")}
                    cursor={"pointer"}
                    w={"fit-content"}
                  >
                    Fase{" "}
                  </Text>
                  {renderSortIcon("fase")}
                </Box>
              </Th>
              <Th minW="150px" textAlign="center" color={theme.text}>
                <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                  <Text
                    alignItems={"center"}
                    fontSize={11}
                    className={getHeaderClass("empreendimentoDeInteresse")}
                    onClick={() => requestSort("empreendimentoDeInteresse")}
                    w={"fit-content"}
                    cursor={"pointer"}
                  >
                    Empreendimento de interesse
                  </Text>
                  {renderSortIcon("empreendimentoDeInteresse")}
                </Box>
              </Th>
              <Th minW="150px" textAlign="center" color={theme.text}>
                <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                  <Text
                    alignItems={"center"}
                    fontSize={11}
                    className={getHeaderClass("dataUltimaModificacao")}
                    onClick={() => requestSort("dataUltimaModificacao")}
                    w={"fit-content"}
                    cursor={"pointer"}
                  >
                    Data de ultima modificação{" "}
                  </Text>
                  {renderSortIcon("dataUltimaModificacao")}
                </Box>
              </Th>
              <Th minW="150px" textAlign="center" color={theme.text}>
                <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                  <Text
                    alignItems={"center"}
                    fontSize={11}
                    className={getHeaderClass("origemConta")}
                    onClick={() => requestSort("origemConta")}
                    w={"fit-content"}
                    cursor={"pointer"}
                  >
                    Origem da conta{" "}
                  </Text>
                </Box>
              </Th>

              <Th
                minW="60px"
                position="sticky"
                right="0"
                zIndex={10}
                bg="white"
                textAlign="center"
              >
                <Text alignItems={"center"} fontSize={11} w={"fit-content"}>
                  Ações{" "}
                </Text>
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {filteredRows?.map((row: any) => (
              <Link
                key={row?.oportunidadeId}
                href="/oportunidades/minhas-oportunidades/detalhes"
                onClick={(e: any) => {
                  if (!e.ctrlKey && !e.metaKey && e.button === 0) {
                    e.preventDefault();

                    localStorage.setItem(
                      "@oportunidade_nova_aba",
                      JSON.stringify({
                        oportunidadeId: row?.oportunidadeId,
                        nomeDaConta: row?.nomeDaConta,
                        opportunityIdSalesforce: row?.opportunityIdSalesforce,
                        fase: row?.fase,
                        empreendimentoDeInteresse:
                          row?.empreendimentoDeInteresse,
                        idEmpreendimentoSales: row?.idEmpreendimentoSales,
                        imobiliaria: row?.imobiliaria,
                        imobiliariaId: row?.imobiliariaId,
                      })
                    );

                    handleClickId(
                      row?.opportunityIdSalesforce,
                      row?.oportunidadeId,
                      row?.nomeDaConta
                    );
                    setFaseOp(
                      row?.fase?.toLocaleLowerCase() === "fechado e ganho" ||
                        row?.fase?.toLocaleLowerCase() === "fechado e perdido"
                    );
                    setNomeEmpreendimentoSales(row?.empreendimentoDeInteresse);
                    setIdEmpreendimentoSales(row?.idEmpreendimentoSales);
                    setImobiliariaNome(row?.imobiliaria);
                    setImobiliariaId(row?.imobiliariaId);
                  }
                }}
                display={"contents"}
              >
                <Tr
                  key={row?.oportunidadeId}
                  _hover={{ bg: "#E2E8F0" }}
                  cursor={"pointer"}
                  onClick={() => {
                    handleClickId(
                      row?.opportunityIdSalesforce,
                      row?.oportunidadeId,
                      row?.nomeDaConta
                    );
                    setFaseOp(
                      row?.fase?.toLocaleLowerCase() === "fechado e ganho" ||
                        row?.fase?.toLocaleLowerCase() === "fechado e perdido"
                    );
                    setNomeEmpreendimentoSales(row?.empreendimentoDeInteresse);
                    setIdEmpreendimentoSales(row?.idEmpreendimentoSales);
                    setImobiliariaNome(row?.imobiliaria);
                    setImobiliariaId(row?.imobiliariaId);
                  }}
                  onMouseDown={() => {
                    localStorage.setItem(
                      "@oportunidade_nova_aba",
                      JSON.stringify({
                        oportunidadeId: row?.oportunidadeId,
                        nomeDaConta: row?.nomeDaConta,
                        opportunityIdSalesforce: row?.opportunityIdSalesforce,
                        fase: row?.fase,
                        empreendimentoDeInteresse:
                          row?.empreendimentoDeInteresse,
                        idEmpreendimentoSales: row?.idEmpreendimentoSales,
                        imobiliaria: row?.imobiliaria,
                        imobiliariaId: row?.imobiliariaId,
                      })
                    );
                  }}
                >
                  <Td minW="150px" color={theme.text} verticalAlign="middle">
                    <Styled.ContainerNameAccount>
                      <Text
                        maxW={{
                          base: "140px",
                          md: "130px",
                          "2xl": "fit-content",
                        }}
                        fontSize={"13px"}
                        fontWeight={"medium"}
                      >
                        {row.nomeDaConta?.toUpperCase()}
                      </Text>
                      {row?.ranking !== null && (
                        <Box
                          display={"flex"}
                          w={"fit-content"}
                          alignItems={"center"}
                          justifyContent={"center"}
                        >
                          <FaAward
                            color={handleRanking(row?.ranking)}
                            fontSize={"16px"}
                          />
                        </Box>
                      )}
                    </Styled.ContainerNameAccount>
                  </Td>

                  <Td minW="150px" color={theme.text50} verticalAlign="middle">
                    <Styled.ContainerIdOportunidade>
                      <a href="/oportunidades/minhas-oportunidades/detalhes">
                        {row?.oportunidadeId}
                      </a>
                    </Styled.ContainerIdOportunidade>
                  </Td>

                  <Td minW="150px" verticalAlign="middle">
                    <Styled.ContainerFase
                      fase={row?.fase?.toLowerCase()}
                      onClick={() => {}}
                    >
                      <span>{handleAdjustFase(row.fase)}</span>
                    </Styled.ContainerFase>
                  </Td>

                  <Td minW="150px" color={theme.text50} verticalAlign="middle">
                    <Styled.ContainerEmpreendimento>
                      <span>{row?.empreendimentoDeInteresse}</span>
                    </Styled.ContainerEmpreendimento>
                  </Td>

                  <Td minW="150px" color={theme.text50} verticalAlign="middle">
                    <Styled.ContainerUnidade>
                      <span>{formatDate(row?.dataUltimaModificacao)}</span>
                    </Styled.ContainerUnidade>
                  </Td>

                  <Td minW="150px" color={theme.text50} verticalAlign="middle">
                    <Styled.ContainerUnidade>
                      <span>{row?.origemConta}</span>
                    </Styled.ContainerUnidade>
                  </Td>

                  <Td
                    minW="60px"
                    position="sticky"
                    right="0"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    verticalAlign="middle"
                  >
                    {loadingCreateCall ? (
                      <Box h={"15px"}>
                        <Spinner size="sm" />
                      </Box>
                    ) : (
                      <ButtonDropdown
                        popoverContent={arrayButtons
                          .filter(
                            (button) =>
                              !(
                                button.title ===
                                  "Fechar e perder oportunidade" &&
                                row.PermitidoQueda === false
                              )
                          )
                          .map((button) => (
                            <Box w="100%" key={button.title}>
                              <Flex
                                flexDir={"column"}
                                w="100%"
                                justifyContent={"center"}
                              >
                                <Box
                                  display={"flex"}
                                  p={"10px"}
                                  gap={"15px"}
                                  alignItems={"center"}
                                  opacity={"0.8"}
                                  _hover={{ opacity: "1" }}
                                  cursor="pointer"
                                  onClick={() =>
                                    handleClickButtonDropdown(row, button.title)
                                  }
                                >
                                  <Box w={"fit-content"} color={theme.text}>
                                    {button.icon}
                                  </Box>
                                  <Text
                                    w={"fit-content"}
                                    fontSize={"13px"}
                                    fontWeight={"medium"}
                                    color={theme.text}
                                    _hover={{ color: theme.primary }}
                                  >
                                    {button.title}
                                  </Text>
                                </Box>

                                <Divider
                                  orientation="horizontal"
                                  borderColor={"#E5E9F0"}
                                  h={"2px"}
                                />
                              </Flex>
                            </Box>
                          ))}
                      />
                    )}
                  </Td>
                </Tr>
              </Link>
            ))}
          </Tbody>
        </CKTable>
      </Box>

      {/* VISUALIZAÇÃO MOBILE EM FORMATO DE CARD - CHAKRA UI */}
      <VStack
        spacing={4}
        display={{ base: "flex", md: "none" }}
        w="full"
        px={4}
        py={2}
      >
        {filteredRows.map((row: any) => {
          const faseColors = getFaseColors(row.fase);

          return (
            <Card
              key={row.oportunidadeId}
              w="full"
              shadow="md"
              borderRadius="12px"
              border="1px solid"
              borderColor="gray.200"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{
                shadow: "lg",
                borderColor: theme.primary,
                transform: "translateY(-2px)",
              }}
              _active={{
                transform: "translateY(0px)",
              }}
              onClick={(e: any) => {
                if (!e.ctrlKey && !e.metaKey && e.button === 0) {
                  e.preventDefault();

                  localStorage.setItem(
                    "@oportunidade_nova_aba",
                    JSON.stringify({
                      oportunidadeId: row?.oportunidadeId,
                      nomeDaConta: row?.nomeDaConta,
                      opportunityIdSalesforce: row?.opportunityIdSalesforce,
                      fase: row?.fase,
                      empreendimentoDeInteresse: row?.empreendimentoDeInteresse,
                      idEmpreendimentoSales: row?.idEmpreendimentoSales,
                      imobiliaria: row?.imobiliaria,
                      imobiliariaId: row?.imobiliariaId,
                    })
                  );

                  handleClickId(
                    row?.opportunityIdSalesforce,
                    row?.oportunidadeId,
                    row?.nomeDaConta
                  );
                  setFaseOp(
                    row?.fase?.toLocaleLowerCase() === "fechado e ganho" ||
                      row?.fase?.toLocaleLowerCase() === "fechado e perdido"
                  );
                  setNomeEmpreendimentoSales(row?.empreendimentoDeInteresse);
                  setIdEmpreendimentoSales(row?.idEmpreendimentoSales);
                  setImobiliariaNome(row?.imobiliaria);
                  setImobiliariaId(row?.imobiliariaId);
                }
              }}
            >
              <CardBody p={4}>
                <Flex justify="space-between" align="center" mb={3}>
                  <HStack spacing={2} flex={1} justify="space-between">
                    <Text
                      w={"fit-content"}
                      fontSize="md"
                      fontWeight="medium"
                      color={theme.text}
                      flex={1}
                    >
                      {row.nomeDaConta?.toUpperCase()}
                    </Text>

                    <Box
                      w={"fit-content"}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <ButtonDropdown
                        popoverContent={arrayButtons
                          .filter(
                            (button) =>
                              !(
                                button.title ===
                                  "Fechar e perder oportunidade" &&
                                row.PermitidoQueda === false
                              )
                          )
                          .map((button) => (
                            <Box w="100%" key={button.title}>
                              <Flex
                                flexDir={"column"}
                                w="100%"
                                justifyContent={"center"}
                              >
                                <Box
                                  display={"flex"}
                                  p={"10px"}
                                  gap={"15px"}
                                  alignItems={"center"}
                                  opacity={"0.8"}
                                  _hover={{ opacity: "1" }}
                                  cursor="pointer"
                                  onClick={() =>
                                    handleClickButtonDropdown(row, button.title)
                                  }
                                >
                                  <Box w={"fit-content"} color={theme.text}>
                                    {button.icon}
                                  </Box>
                                  <Text
                                    w={"fit-content"}
                                    fontSize={"13px"}
                                    fontWeight={"medium"}
                                    color={theme.text}
                                    _hover={{ color: theme.primary }}
                                  >
                                    {button.title}
                                  </Text>
                                </Box>

                                <Divider
                                  orientation="horizontal"
                                  borderColor={"#E5E9F0"}
                                  h={"2px"}
                                />
                              </Flex>
                            </Box>
                          ))}
                      />
                    </Box>
                  </HStack>
                  {row?.ranking && (
                    <Icon
                      as={FaAward}
                      color={handleRanking(row.ranking)}
                      boxSize={5}
                    />
                  )}
                </Flex>

                <Flex justify="center" mb={4}>
                  <Badge
                    bg={faseColors.bg}
                    color={faseColors.color}
                    borderColor={faseColors.borderColor}
                    border="1px solid"
                    borderRadius="full"
                    px={3}
                    py={1}
                    fontSize="xs"
                    fontWeight="semibold"
                    textAlign="center"
                    minW="120px"
                  >
                    {handleAdjustFase(row.fase)}
                  </Badge>
                </Flex>

                <VStack>
                  <HStack spacing={2}>
                    <Text
                      fontSize="xs"
                      color={theme.text50}
                      fontWeight="medium"
                      w={"fit-content"}
                    >
                      ID da Oportunidade:
                    </Text>
                    <Text
                      fontSize="xs"
                      color={theme.primary}
                      fontWeight="medium"
                      w={"fit-content"}
                    >
                      {row.oportunidadeId}
                    </Text>
                  </HStack>

                  <Divider />

                  <HStack spacing={2} align="flex-start">
                    <HStack spacing={1} align="flex-start" flex={1}>
                      <Text
                        fontSize="xs"
                        color={theme.text50}
                        fontWeight="medium"
                        w={"fit-content"}
                      >
                        Empreendimento de interesse:
                      </Text>
                      <Text
                        fontSize="sm"
                        color={theme.text}
                        lineHeight="short"
                        w={"fit-content"}
                      >
                        {row.empreendimentoDeInteresse}
                      </Text>
                    </HStack>
                  </HStack>

                  <Divider />

                  <VStack spacing={2} align="stretch">
                    <HStack spacing={2}>
                      <Text
                        fontSize="xs"
                        color={theme.text50}
                        fontWeight="medium"
                        w={"fit-content"}
                      >
                        Data de ultima modificação:
                      </Text>
                      <Text fontSize="xs" color={theme.text} w={"fit-content"}>
                        {formatDate(row.dataUltimaModificacao)}
                      </Text>
                    </HStack>

                    <HStack spacing={2}>
                      <Text
                        fontSize="xs"
                        color={theme.text50}
                        fontWeight="medium"
                        w={"fit-content"}
                      >
                        Origem da conta:
                      </Text>
                      <Text fontSize="xs" color={theme.text} w={"fit-content"}>
                        {row.origemConta}
                      </Text>
                    </HStack>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          );
        })}
      </VStack>
    </>
  );
}

export default React.memo(TableMyOportunity);
